import React from 'react';
import { ExternalLink, ArrowRight } from 'lucide-react';
import {
  PageHeader,
  ShimmerLoader,
  LinkButton,
  PageState,
  PageErrorState,
  Reveal,
} from '@/shared/ui';
import { HeroSection } from './components/hero-section';
import { SkillCategory } from './components/skill-category';
import { ContactCard } from './components/contact-card';
import {
  useProfile,
  useSiteContent,
  useSkills,
} from '@personal-portfolio/queries';
import { getIconFromType } from '@/shared/lib/icons';
import { useLocation } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import { toErrorMessage } from '@/shared/lib/query-error';

export const AboutPage: React.FC = () => {
  const {
    data: profileData,
    isPending: profileLoading,
    isFetching: profileFetching,
    isPaused: profilePaused,
    error: profileError,
    refetch: refetchProfile,
  } = useProfile();
  const {
    data: skillsData,
    error: skillsError,
    isFetching: skillsFetching,
    isPaused: skillsPaused,
    refetch: refetchSkills,
  } = useSkills();
  const error = toErrorMessage(
    profileError ?? skillsError,
    profilePaused || skillsPaused,
  );
  const location = useLocation();
  const content = useSiteContent();
  const about = content.pages.about;
  // The profile is the preferred source for the hero copy; the site content
  // defaults cover every state where it is not (yet) available.
  const pageTitle = profileData?.name ?? content.site.name;
  const pageSubtitle = profileData?.headline ?? about.subtitle;

  const handleRetry = () => {
    if (profileFetching || skillsFetching) return;
    void refetchProfile();
    void refetchSkills();
  };

  const renderLoading = () => (
    <>
      <PageHeader hero title={pageTitle} subtitle={pageSubtitle} />

      <div className="mb-16">
        <ShimmerLoader lines={2} height="h-8" className="mb-4" />
        <ShimmerLoader lines={4} height="h-4" className="mb-6" />
        <div className="flex gap-4">
          <ShimmerLoader lines={1} height="h-10" className="w-32" />
          <ShimmerLoader lines={1} height="h-10" className="w-32" />
        </div>
      </div>

      <section className="mb-16">
        <ShimmerLoader lines={1} height="h-8" className="mb-8" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-border p-6">
              <ShimmerLoader lines={1} height="h-6" className="mb-4" />
              <ShimmerLoader lines={3} height="h-4" className="mb-2" />
              <ShimmerLoader lines={2} height="h-4" className="w-3/4" />
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <ShimmerLoader lines={1} height="h-8" className="mb-8" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-border p-4">
              <ShimmerLoader lines={1} height="h-6" className="mb-2" />
              <ShimmerLoader lines={1} height="h-4" className="w-2/3" />
            </div>
          ))}
        </div>
      </section>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader hero title={pageTitle} subtitle={pageSubtitle} />
      <PageErrorState onRetry={handleRetry} message={error ?? undefined} />
    </>
  );

  const renderContent = () => {
    if (!profileData) {
      return (
        <>
          <PageHeader hero title={pageTitle} subtitle={pageSubtitle} />
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-6xl text-muted-foreground">
                <span role="img" aria-label="Developer">
                  👨‍💻
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                {about.empty.title}
              </h3>
              <p className="text-muted-foreground">{about.empty.message}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader hero title={pageTitle} subtitle={pageSubtitle} />

        {profileData.summary && (
          <HeroSection
            description={profileData.summary}
            showProfilePicture={true}
            avatarUrl={profileData.avatar?.url}
            avatarAlt={profileData.name}
            actions={
              <>
                <LinkButton href="/experience" className="group">
                  {about.viewExperienceCta}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </LinkButton>
                <LinkButton variant="ghost" href="/projects" className="group">
                  {about.exploreProjectsCta}
                  <ExternalLink
                    size={16}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </LinkButton>
              </>
            }
          />
        )}

        {skillsData && (
          <section id="technical-expertise" className="mb-16">
            <h2 className="mb-6 font-mono text-sm font-medium tracking-widest text-muted-foreground uppercase">
              {about.expertiseHeading}
            </h2>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {Object.entries(skillsData).map(([key, skills], index) => (
                <Reveal key={key} index={index} className="h-full">
                  <SkillCategory
                    title={key}
                    skills={skills.map((skill) => skill.name)}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {(profileData.phone || profileData.email) && (
          <section id="get-in-touch" className="mb-8">
            <h2 className="mb-6 font-mono text-sm font-medium tracking-widest text-muted-foreground uppercase">
              {about.contactHeading}
            </h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {[
                ...(profileData.email
                  ? [
                      {
                        href: `mailto:${profileData.email}`,
                        icon: getIconFromType('email'),
                        title: 'Email',
                        description: profileData.email,
                      },
                    ]
                  : []),
                ...(profileData.phone
                  ? [
                      {
                        href: `tel:${profileData.phone}`,
                        icon: getIconFromType('phone'),
                        title: 'Phone',
                        description: profileData.phone,
                      },
                    ]
                  : []),
                ...(profileData.socialLinks?.map((link) => ({
                  href: link.url,
                  icon: getIconFromType(link.type || ''),
                  title: link.label || '',
                  description: link.url || '',
                })) ?? []),
              ].map((card, index) => (
                <Reveal
                  key={card.href || index}
                  index={index}
                  className="h-full"
                >
                  <ContactCard {...card} />
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {location.pathname === '/' ? content.site.metaTitle : about.metaTitle}
        </title>
        {/* "/" and "/about" serve the same content, so both canonicalize to
            the site root. */}
        <link rel="canonical" href={`${content.site.url}/`} />
      </Helmet>
      <PageState
        loading={profileLoading}
        error={error}
        hasData={!!profileData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
};
